// KPI P2 §11.2: an erased person's pseudonym (…@erased.invalid) and user id (erased:…). Their
// NAMES need no client-side handling: the server's erasure rewrites every stored name for the
// person (players, role names, observers, facilitators, creator, chat) to "Erased participant",
// so every screen that renders session data already shows it. What the client must do is keep
// those identities out of NEW exercises (below).
export const ERASED_PARTICIPANT = "Erased participant"

export const isErasedIdentity = (value) => {
  const text = String(value ?? "")
    .trim()
    .toLowerCase()
  return text.startsWith("erased:") || /@erased\.invalid$/.test(text)
}

// An Independent-group member session's person: the first role's assignee, else the first player.
// Shared with groupRosterFromSession so the erasure filter and the roster rebuild agree.
export const memberPrimaryEmail = (member) => {
  const anchorRole = Object.keys(member?.roleAssignments ?? {})[0] ?? null
  return (anchorRole ? member?.roleAssignments?.[anchorRole] : null) ?? member?.players?.[0]?.email ?? ""
}

// KPI P2 §10 / §11.4 (Q54): Duplicate and Restart never carry an erased participant into a new
// exercise. Returns the session without erased roster entries and how many were dropped.
const erasedEmail = (e) => isErasedIdentity(e)
export const withoutErasedParticipants = (session) => {
  if (!session || typeof session !== "object") return { session, removed: 0 }
  let removed = 0
  const count = (before, after) => {
    removed += before - after
  }
  const out = { ...session }
  if (session.roleAssignments && typeof session.roleAssignments === "object") {
    const entries = Object.entries(session.roleAssignments)
    const kept = entries.filter(([, e]) => !erasedEmail(e))
    count(entries.length, kept.length)
    out.roleAssignments = Object.fromEntries(kept)
    if (session.roleAssigneeNames)
      out.roleAssigneeNames = Object.fromEntries(
        Object.entries(session.roleAssigneeNames).filter(([role]) => role in out.roleAssignments || !(role in session.roleAssignments))
      )
  }
  if (Array.isArray(session.players)) {
    out.players = session.players.filter((p) => !erasedEmail(p?.email))
    count(session.players.length, out.players.length)
  }
  if (Array.isArray(session.roleAssigneeEmails)) {
    out.roleAssigneeEmails = session.roleAssigneeEmails.map((e) => (erasedEmail(e) ? "" : e))
  }
  if (Array.isArray(session.spectators)) out.spectators = session.spectators.filter((s) => !erasedEmail(s?.email))
  if (Array.isArray(session.reviewers)) out.reviewers = session.reviewers.filter((e) => !erasedEmail(e))
  if (Array.isArray(session.sessions)) {
    // A member whose person was erased is dropped whole — clearing its roster alone left the
    // member in place, and the roster rebuild then made it a blank participant row.
    const kept = session.sessions.filter((member) => !erasedEmail(memberPrimaryEmail(member)))
    removed += session.sessions.length - kept.length
    out.sessions = kept.map((member) => {
      const r = withoutErasedParticipants(member)
      removed += r.removed
      return r.session
    })
  }
  return { session: out, removed }
}
